// Rysowanie toru: statyczne przeszkody, linia mety i znacznik srodka masy.

import type { ChallengeDefinition } from '../challenges/step';
import type { Camera } from './camera';

export interface WorldDrawOptions {
  comMarker?: { x: number; y: number } | null;
}

/** Pozycja i rozmiar przeszkody do rysowania (srodek w metrach, kat w radianach). */
export interface StartMarkerOptions {
  /** Srodek znacznika w metrach (os X). */
  x: number;
  /** Wysokosc gornej powierzchni podloza (m) - znacznik na nim stoi. */
  groundY: number;
  /** Promien znacznika w metrach (np. kolo). */
  radius: number;
  label?: string;
}

/**
 * Znacznik startu na pustej trasie: przerywany obrys kola (z piasta) i podpis.
 * Pokazuje, gdzie stanie konstrukcja gracza, zanim jeszcze cokolwiek zbuduje.
 */
export function drawStartMarker(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  options: StartMarkerOptions,
): void {
  const center = camera.worldToScreen(options.x, options.groundY + options.radius);
  const radiusPx = Math.max(7, options.radius * camera.scale);

  ctx.save();

  ctx.setLineDash([6, 5]);
  ctx.strokeStyle = 'rgba(255, 212, 121, 0.85)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(center.x, center.y, radiusPx, 0, Math.PI * 2);
  ctx.stroke();

  // Piasta - zeby czytalo sie jak kolo.
  ctx.setLineDash([]);
  ctx.fillStyle = 'rgba(255, 212, 121, 0.85)';
  ctx.beginPath();
  ctx.arc(center.x, center.y, 3, 0, Math.PI * 2);
  ctx.fill();

  if (options.label) {
    ctx.fillStyle = 'rgba(255, 212, 121, 0.9)';
    ctx.font = '12px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(options.label, center.x, center.y - radiusPx - 6);
  }

  ctx.restore();
}

/** Pozycja i rozmiar przeszkody do rysowania (srodek w metrach, kat w radianach). */
export interface PropPose {
  x: number;
  y: number;
  angle: number;
  w: number;
  h: number;
}

/** Rysuje dynamiczne przeszkody (skrzynki, kladki) podanym obrotem i pozycja. */
export function drawProps(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  props: readonly PropPose[],
): void {
  for (const prop of props) {
    const halfW = prop.w / 2;
    const halfH = prop.h / 2;
    const cos = Math.cos(prop.angle);
    const sin = Math.sin(prop.angle);
    const local: readonly [number, number][] = [
      [-halfW, -halfH],
      [halfW, -halfH],
      [halfW, halfH],
      [-halfW, halfH],
    ];
    const screen = local.map(([lx, ly]) => {
      const worldX = prop.x + lx * cos - ly * sin;
      const worldY = prop.y + lx * sin + ly * cos;
      return camera.worldToScreen(worldX, worldY);
    });
    const first = screen[0];
    if (!first) continue;

    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < screen.length; i += 1) {
      const point = screen[i];
      if (point) ctx.lineTo(point.x, point.y);
    }
    ctx.closePath();

    // Kolor: deska (wydluzona) na szaro-brazowo, skrzynka na cieplo-brazowo.
    const isPlank = prop.w > prop.h * 3;
    ctx.fillStyle = isPlank ? '#6b6152' : '#9c7a45';
    ctx.fill();
    ctx.strokeStyle = isPlank ? '#403a30' : '#5c4527';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Kreska "wewnatrz" dla czytelnosci obrotu.
    const center = camera.worldToScreen(prop.x, prop.y);
    ctx.beginPath();
    ctx.moveTo(center.x, center.y);
    ctx.lineTo(
      camera.worldToScreen(prop.x + cos * halfW, prop.y + sin * halfW).x,
      camera.worldToScreen(prop.x + cos * halfW, prop.y + sin * halfW).y,
    );
    ctx.strokeStyle = isPlank ? '#403a30' : '#5c4527';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

export function drawChallenge(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  challenge: ChallengeDefinition,
  options: WorldDrawOptions = {},
): void {
  for (const box of challenge.staticBoxes) {
    const minX = box.x - box.w / 2;
    const maxX = box.x + box.w / 2;
    const topY = box.y + box.h / 2;
    const bottomY = box.y - box.h / 2;
    const topLeft = camera.worldToScreen(minX, topY);
    const bottomRight = camera.worldToScreen(maxX, bottomY);
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    const gradient = ctx.createLinearGradient(0, topLeft.y, 0, bottomRight.y);
    gradient.addColorStop(0, '#2f3a4a');
    gradient.addColorStop(1, '#222b38');
    ctx.fillStyle = gradient;
    ctx.fillRect(topLeft.x, topLeft.y, width, height);

    // Linia gruntu na gornej krawedzi - czytelny punkt odniesienia.
    ctx.strokeStyle = '#46566e';
    ctx.lineWidth = 1;
    ctx.strokeRect(topLeft.x, topLeft.y, width, height);
    ctx.beginPath();
    ctx.moveTo(topLeft.x, topLeft.y);
    ctx.lineTo(bottomRight.x, topLeft.y);
    ctx.strokeStyle = '#5b6d88';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // Linia mety - cala konstrukcja musi ja przekroczyc.
  const finishTop = camera.worldToScreen(challenge.finishX, challenge.platformTopY + 1.1);
  const finishBottom = camera.worldToScreen(challenge.finishX, challenge.platformTopY - 0.04);
  ctx.save();
  ctx.strokeStyle = '#ffd479';
  ctx.lineWidth = 2;
  ctx.setLineDash([9, 6]);
  ctx.beginPath();
  ctx.moveTo(finishTop.x, finishTop.y);
  ctx.lineTo(finishBottom.x, finishBottom.y);
  ctx.stroke();
  ctx.restore();

  if (options.comMarker) {
    const marker = camera.worldToScreen(options.comMarker.x, options.comMarker.y);
    const ground = camera.worldToScreen(options.comMarker.x, challenge.platformTopY);

    // Pion do podloza: od razu widac, czy srodek masy jest przed kolami.
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 212, 121, 0.45)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(marker.x, marker.y);
    ctx.lineTo(ground.x, ground.y);
    ctx.stroke();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(marker.x, marker.y, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#ffd479';
    ctx.fill();
    ctx.strokeStyle = '#5c4a17';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}